import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import CoachMessage from './CoachMessage';
import CoachIntro from './CoachIntro';
import OwnerMessage from './OwnerMessage';
import AppEventNote from './AppEventNote';
import TypingIndicator from './TypingIndicator';
import ToolProgress from './ToolProgress';
import SuggestedReplies from './SuggestedReplies';
import Composer from './Composer';
import MessageArtifacts from './MessageArtifacts';
import ValuationScenario from './ValuationScenario';
import ConfirmationSheet from './ConfirmationSheet';
import { COPY, DEFAULT_COACH_NAME } from './copy';
import { shortDate } from './format';

function timeLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${shortDate(iso)} · ${time}`;
}

// Space kept above a reply that starts out of view.
const REPLY_TOP_GAP = 16;

// The conversation column (PRD §6.1–6.3): message list that follows new
// messages (a long reply is shown from its start), with a "New message" pill, the greeting as the opening hero (D01),
// coach turns with inline artifacts and proposals, thinking/tool progress,
// the reply as it streams in,
// then the dock: suggested replies, composer and the standing disclaimer
// (FR8). Data and actions come from the container.
export default function GeorgeConversation({
  conversation,
  pending,
  progressRows,
  streamingText,
  error,
  onSend,
  onRetry,
  onAskAbout,
  onRate,
  onActivateValuation,
  onIntroducePartner,
  onPartnerNotNow,
  onConfirmToolCall,
  onDeclineToolCall,
  renderProposals,
  draft,
  onDraftConsumed,
}) {
  const coachName = conversation?.coach_name || DEFAULT_COACH_NAME;
  const messages = conversation?.messages || [];
  const listRef = useRef(null);
  const [pinned, setPinned] = useState(true);
  const [unseen, setUnseen] = useState(false);
  const lastCount = useRef(messages.length);
  const lastMessage = messages[messages.length - 1];

  // Follow the newest coach reply, but never past its first line: a reply
  // taller than the screen (text and cards) is shown from its start, so the
  // owner reads the coach's words before the cards under them.
  const followTarget = (el) => {
    const replies = el.querySelectorAll('[data-reply]');
    const reply = replies[replies.length - 1];
    const bottom = el.scrollHeight - el.clientHeight;
    if (!reply) return bottom;
    const start = reply.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - REPLY_TOP_GAP;
    return Math.max(0, Math.min(bottom, start));
  };

  const followReply = (smooth = false) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: followTarget(el), behavior: smooth ? 'smooth' : 'auto' });
    setUnseen(false);
  };

  useLayoutEffect(() => {
    if (messages.length !== lastCount.current) {
      // The owner's own message always comes into view.
      if (pinned || lastMessage?.role === 'user') followReply(true);
      else setUnseen(true);
      lastCount.current = messages.length;
    }
  }, [messages.length]);

  useEffect(() => {
    if (pinned) followReply();
  }, [pending, (progressRows || []).length, streamingText]);

  useEffect(() => {
    followReply();
  }, []);

  // Following while the owner is at the bottom, or still at the start of
  // the reply we brought into view.
  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    const following = atBottom || Math.abs(el.scrollTop - followTarget(el)) < 80;
    setPinned(following);
    if (following) setUnseen(false);
  };

  const opening = messages.every((m) => m.kind === 'greeting');
  const showReplies = !pending && lastMessage && lastMessage.role === 'agent' && (lastMessage.suggested_replies || []).length > 0;

  return (
    <section className="flex flex-col h-full min-h-0" aria-label={`Conversation with ${coachName}`}>
      <div
        ref={listRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto pt-6 md:pt-10 px-4 md:px-8 pb-2 md:pb-6"
      >
        {/* min-h-full + justify-end: a short conversation sits just above
            the composer, as in the designs, and grows upward. */}
        <div
          className="mx-auto flex flex-col justify-end min-h-full max-w-[760px] gap-[22px] md:gap-7"
          aria-live="polite"
          aria-relevant="additions"
        >
          {messages.map((m) => {
            if (m.role === 'user') return <OwnerMessage key={m.id} content={m.content} time={timeLabel(m.created_at)} />;
            if (m.role === 'system') return m.owner_note ? <AppEventNote key={m.id} note={m.owner_note} /> : null;
            if (m.kind === 'greeting') {
              return <CoachIntro key={m.id} coachName={coachName} coachRole={conversation?.coach_role} content={m.content} />;
            }
            return (
              <CoachMessage
                key={m.id}
                reply={m.id === lastMessage?.id}
                coachName={coachName}
                content={m.content}
                time={timeLabel(m.created_at)}
                rating={m.rating}
                onRate={onRate && m.kind !== 'greeting' ? (value) => onRate(m, value) : null}
              >
                <MessageArtifacts
                  artifacts={m.artifacts || []}
                  partnerRecommendations={m.partner_recommendations || []}
                  coachName={coachName}
                  onAsk={onAskAbout}
                  onIntroducePartner={onIntroducePartner}
                  onPartnerNotNow={onPartnerNotNow}
                  pending={pending}
                />
                {(m.valuation_scenarios || []).map((s) => (
                  <ValuationScenario key={s.valuation_snapshot_id} scenario={s} onActivate={onActivateValuation} />
                ))}
                {(m.confirmations || []).map((c) => (
                  <ConfirmationSheet key={c.id} confirmation={c} onConfirm={onConfirmToolCall} onDecline={onDeclineToolCall} />
                ))}
                {renderProposals && (m.proposed_actions || []).length > 0 && renderProposals(m, conversation)}
              </CoachMessage>
            );
          })}
          {pending && (progressRows || []).length > 0 && <ToolProgress rows={progressRows} />}
          {pending && streamingText && (
            // Hidden from screen readers: the saved message is announced once,
            // when it replaces this, instead of every streamed fragment.
            <div aria-hidden="true" data-testid="streaming-reply" data-reply>
              <CoachMessage coachName={coachName} content={streamingText} />
            </div>
          )}
          {pending && !streamingText && <TypingIndicator coachName={coachName} />}
          {error && (
            <ToolProgress rows={[{ key: 'error', state: 'error', label: error || COPY.error(coachName) }]} onRetry={onRetry} />
          )}
        </div>
      </div>

      {unseen && (
        <div className="relative flex justify-center -mt-12 mb-2">
          <button
            type="button"
            onClick={() => followReply(true)}
            className="inline-flex items-center gap-1.5 min-h-9 px-3.5 rounded-full border border-border bg-card text-foreground text-[13px] cursor-pointer"
          >
            {COPY.newMessage}
            <ArrowDown size={14} />
          </button>
        </div>
      )}

      <div className="pt-2 md:pt-3 px-4 md:px-8 pb-4">
        <div className="mx-auto flex flex-col gap-3 max-w-[760px]">
          {showReplies && <SuggestedReplies replies={lastMessage.suggested_replies} onPick={onSend} disabled={pending} />}
          <Composer
            placeholder={opening ? COPY.openingPlaceholder(coachName) : undefined}
            coachName={coachName}
            onSend={onSend}
            disabled={!!pending}
            statusText={pending ? COPY.thinking(coachName) : ''}
            draft={draft}
            onDraftConsumed={onDraftConsumed}
          />
          <p className="m-0 text-center text-[12px] leading-normal text-muted-foreground">
            {COPY.disclaimer(coachName, conversation?.brand_name)}
          </p>
        </div>
      </div>
    </section>
  );
}
